import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  DurationPolicyOverview,
  getResponsibleGamingOverview,
  RecurringLimitOverview,
  RecurringPeriod,
  ResponsibleGamingOverview,
  resetResponsibleGamingPolicy,
  setResponsibleGamingPolicy,
} from "api/responsibleGaming";
import { useAppDispatch, useAppSelector } from "store";
import { handleApiError } from "helpers/handleApiError";
import { TimeFormat, useTimeFormat } from "helpers/timeFormat";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import Tooltip from "components/Tooltip";
import ConfirmTooltip from "components/confirmTooltip/ConfirmTooltip";
import Loader from "components/global/Loader";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { ReactComponent as DeleteIcon } from "assets/icons/bin-icon.svg";
import styles from "./ResponsibleGamingSection.module.scss";

const periods: { key: string; value: RecurringPeriod }[] = [
  { key: "daily", value: "DAY" },
  { key: "weekly", value: "WEEK" },
  { key: "monthly", value: "MONTH" },
];
const coolingOffOptions = [
  ["24_HOURS", "24 hours"],
  ["7_DAYS", "7 days"],
  ["1_MONTH", "1 month"],
  ["3_MONTHS", "3 months"],
].map(([value, label]) => ({ value, label }));
const selfExclusionOptions = [
  ["1_YEAR", "1 year"],
  ["3_YEARS", "3 years"],
  ["5_YEARS", "5 years"],
  ["10_YEARS", "10 years"],
  ["FOREVER", "Forever"],
].map(([value, label]) => ({ value, label }));

const formatDurationLabel = (value: string) => {
  if (value === "LIFETIME") return "Lifetime";
  if (value === "FOREVER") return "Forever";

  const [amount, rawUnit] = value.split("_");
  const unit = rawUnit.toLowerCase();
  const singular = unit.endsWith("s") ? unit.slice(0, -1) : unit;
  return `${amount} ${amount === "1" ? singular : unit}`;
};

const formatDate = (value: string | undefined, timeFormat: TimeFormat) =>
  value
    ? moment(value).format(
        timeFormat === "12h" ? "DD.MM.YY hh:mm A" : "DD.MM.YY HH:mm",
      )
    : "";
const formatNumericValue = (value?: string | number) => {
  if (value === undefined || value === null || value === "") return "";
  return String(value)
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0+$/, "");
};
const secondsToHours = (seconds?: number) =>
  seconds === undefined ? "" : formatNumericValue((seconds / 3600).toFixed(4));
const formatStatusValue = (value: string) => {
  const [integer, fraction] = value.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
};
const hoursToSeconds = (hours: string) => Math.round(Number(hours) * 3600);
const getLimits = (
  overview: ResponsibleGamingOverview | null,
  type: "deposit" | "loss" | "time",
) => {
  if (!overview) return [];
  const plural = overview[`${type}Limits` as keyof ResponsibleGamingOverview];
  const singular = overview[`${type}Limit` as keyof ResponsibleGamingOverview];
  const container = plural ?? singular;

  if (Array.isArray(container)) return container;
  if (!container || typeof container !== "object") return [];

  const scopes: RecurringLimitOverview[] = [];
  const visit = (value: unknown, inferredPeriod?: RecurringPeriod) => {
    if (!value || typeof value !== "object") return;
    const candidate = value as Partial<RecurringLimitOverview>;
    const recurringPeriod = candidate.recurringPeriod ?? inferredPeriod;
    if (
      recurringPeriod &&
      ("configured" in candidate || "currentPolicy" in candidate)
    ) {
      scopes.push({
        ...(candidate as RecurringLimitOverview),
        recurringPeriod,
      });
      return;
    }
    Object.entries(value).forEach(([key, nested]) =>
      visit(
        nested,
        key === "DAY" || key === "WEEK" || key === "MONTH" ? key : undefined,
      ),
    );
  };

  visit(container);
  return scopes;
};

const RemoveButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  label: string;
  tooltipId?: string;
}> = ({ onClick, disabled, label, tooltipId }) => (
  <button
    className={styles.removeButton}
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    data-tooltip-id={tooltipId}
  >
    <DeleteIcon />
  </button>
);

interface LimitCardProps {
  fieldErrors: Record<string, string>;
  clearFieldError: (key: string) => void;
  id: "deposit" | "loss" | "time";
  title: string;
  tooltip: string;
  scopes: RecurringLimitOverview[];
  currency?: string;
  busy: boolean;
  disabled?: boolean;
  values: Record<string, string>;
  onChange: (period: RecurringPeriod, value: string) => void;
  onReset: (policyId: string) => void;
  resetPolicyId?: string;
  resetLoading: boolean;
}

const LimitCard: React.FC<LimitCardProps> = ({
  id,
  title,
  tooltip,
  scopes,
  currency,
  busy,
  disabled,
  values,
  onChange,
  onReset,
  resetPolicyId,
  resetLoading,
  fieldErrors,
  clearFieldError,
}) => {
  const { t } = useTranslation();
  const timeFormat = useTimeFormat();
  const isResetting =
    resetLoading &&
    scopes.some((scope) => scope.currentPolicy?.policyId === resetPolicyId);

  const getStatus = (scope?: RecurringLimitOverview) => {
    const pending = scope?.pendingPolicy;
    const windowEnd = scope?.currentWindowEnd;
    if (!pending && !windowEnd) return null;
    if (pending?.changeType === "DISABLE")
      return {
        date: `${t("willBeDisabledOn")} ${formatDate(
          windowEnd ?? pending.effectiveAt,
          timeFormat,
        )}`,
      };
    if (!pending)
      return {
        date: `${t("until").toLowerCase()} ${formatDate(windowEnd, timeFormat)}`,
      };
    const isTimeLimit = id === "time";
    const value = isTimeLimit
      ? secondsToHours(pending.timeLimitSeconds)
      : formatNumericValue(pending.amount);
    const unit = isTimeLimit
      ? ` ${t("hoursShort")}`
      : ` ${pending.currency ?? currency ?? ""}`;
    return {
      value: `${formatStatusValue(value)}${unit}`,
      date: `${t("until").toLowerCase()} ${formatDate(
        windowEnd ?? pending.effectiveAt,
        timeFormat,
      )}`,
    };
  };
  return (
    <section
      className={`${styles.card} ${isResetting ? styles.cardLoading : ""}`}
      aria-busy={isResetting}
    >
      <div className={styles.cardHeader}>
        <h4 className={styles.cardTitle}>
          {title}
          <Tooltip text={tooltip} position="right">
            <span className="info-icon">ℹ</span>
          </Tooltip>
        </h4>
      </div>
      <div className={styles.limitFields}>
        {periods.map(({ key, value: period }) => {
          const scope = scopes.find((item) => item.recurringPeriod === period);
          const status = getStatus(scope);
          const policyId = scope?.currentPolicy?.policyId;
          const scopeDisabled =
            disabled || busy || Boolean(scope?.pendingPolicy);
          return (
            <div className={styles.limitField} key={period}>
              <div className={styles.limitInputControl}>
                <InnerInput
                  value={values[period] ?? ""}
                  placeholder="0"
                  label={t(key)}
                  type="number"
                  min={0}
                  inputMode="decimal"
                  suffix={id === "time" ? t("hoursShort") : currency}
                  id={`responsible-gaming-${id}-${key}`}
                  hasError={Boolean(fieldErrors[`${id}.${period}`])}
                  onChange={(next) => {
                    clearFieldError(`${id}.${period}`);
                    onChange(period, next);
                  }}
                  disabled={scopeDisabled}
                />
                {policyId && (
                  <RemoveButton
                    label={t("resetResponsibleGamingLimit")}
                    onClick={() => onReset(policyId)}
                    disabled={
                      disabled ||
                      busy ||
                      (resetLoading && resetPolicyId === policyId)
                    }
                    tooltipId={`responsible-gaming-reset-tooltip-${policyId}`}
                  />
                )}
              </div>
              {status && (
                <div className={styles.policyStatus}>
                  {status.value && (
                    <span className={styles.policyValue}>{status.value}</span>
                  )}
                  <span className={styles.expirationDate}>{status.date}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isResetting && (
        <div className={styles.cardLoadingOverlay}>
          <Loader />
        </div>
      )}
    </section>
  );
};

const DurationCard: React.FC<{
  id: "coolingOff" | "selfExclusion";
  title: string;
  tooltip: string;
  overview?: DurationPolicyOverview;
  options: { value: string; label: string }[];
  busy: boolean;
  disabled?: boolean;
  value?: string;
  onSelect: (value: string) => void;
  onReset: (policyId: string) => void;
  resetPolicyId?: string;
  resetLoading: boolean;
}> = ({
  id,
  title,
  tooltip,
  overview,
  options,
  busy,
  disabled,
  value,
  onSelect,
  onReset,
  resetPolicyId,
  resetLoading,
}) => {
  const { t } = useTranslation();
  const timeFormat = useTimeFormat();
  const endAt =
    id === "coolingOff" ? overview?.expiresAt : overview?.minimumEndAt;
  const isResetting =
    resetLoading && overview?.currentPolicy?.policyId === resetPolicyId;
  return (
    <section
      className={`${styles.card} ${isResetting ? styles.cardLoading : ""}`}
      aria-busy={isResetting}
    >
      <div className={styles.cardHeader}>
        <h4 className={styles.cardTitle}>
          {title}
          <Tooltip text={tooltip} position="right">
            <span className="info-icon">ℹ</span>
          </Tooltip>
        </h4>
        {overview?.currentPolicy?.policyId && (
          <RemoveButton
            label={t("resetResponsibleGamingLimit")}
            onClick={() => onReset(overview.currentPolicy!.policyId)}
            disabled={
              disabled ||
              busy ||
              (resetLoading &&
                resetPolicyId === overview.currentPolicy!.policyId)
            }
            tooltipId={`responsible-gaming-reset-tooltip-${overview.currentPolicy!.policyId}`}
          />
        )}
      </div>
      <div
        className={`${styles.durationControl} ${
          id === "coolingOff" ? styles.coolingOffControl : ""
        }`}
      >
        <DefaultDropdown
          value={value}
          selectedLabel={value ? formatDurationLabel(value) : undefined}
          placeholder={t("selectDuration")}
          options={options}
          onSelect={(next) => onSelect(String(next))}
          disabled={disabled || busy}
          useFixedMenu
        />
        {endAt && (
          <span className={styles.expirationDate}>
            {t("until").toLowerCase()} {formatDate(endAt, timeFormat)}
          </span>
        )}
      </div>
      {id === "selfExclusion" && overview && (
        <p className={styles.effectiveDecision}>
          {overview.blocking || overview.currentPolicy
            ? t("selfExclusionEffective", {
                effective:
                  formatDate(
                    overview.effectiveAt || overview.currentPolicy?.effectiveAt,
                    timeFormat,
                  ) || t("selfExclusionNoEnd"),
                minimumEnd:
                  formatDate(overview.minimumEndAt, timeFormat) ||
                  t("selfExclusionNoEnd"),
                duration: overview.currentPolicy?.selfExclusionDuration
                  ? formatDurationLabel(
                      overview.currentPolicy.selfExclusionDuration,
                    )
                  : t("selfExclusionNoEnd"),
              })
            : t("selfExclusionNone")}
        </p>
      )}
      {isResetting && (
        <div className={styles.cardLoadingOverlay}>
          <Loader />
        </div>
      )}
    </section>
  );
};

export type ResponsibleGamingSaveHandler = () => Promise<boolean>;

interface ResponsibleGamingSectionProps {
  disabled?: boolean;
  onSaveHandlerChange?: (handler: ResponsibleGamingSaveHandler | null) => void;
}

const emptyLimitDrafts = {
  deposit: {} as Record<string, string>,
  loss: {} as Record<string, string>,
  time: {} as Record<string, string>,
};

const ResponsibleGamingSection: React.FC<ResponsibleGamingSectionProps> = ({
  disabled,
  onSaveHandlerChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const player = useAppSelector((store) => store.players.payload);
  const userId = searchParams.get("entityId") ?? player?.id?.toString();
  const currency = player?.currency;
  const [overview, setOverview] = useState<ResponsibleGamingOverview | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [limitDrafts, setLimitDrafts] = useState(emptyLimitDrafts);
  const [limitErrors, setLimitErrors] = useState<Record<string, string>>({});
  const preserveDraftsOnRefresh = useRef(false);
  const clearLimitError = (key: string) =>
    setLimitErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  const [resetPolicyId, setResetPolicyId] = useState<string>();
  const [resetConfirmationPolicyId, setResetConfirmationPolicyId] =
    useState<string>();
  const [resetting, setResetting] = useState(false);
  const [coolingOffDraft, setCoolingOffDraft] = useState<string>();
  const [selfExclusionDraft, setSelfExclusionDraft] = useState<string>();
  const showError = useCallback(
    (error: unknown) => handleApiError({ error, t, dispatch }),
    [dispatch, t],
  );
  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      setOverview(await getResponsibleGamingOverview(userId));
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [showError, userId]);
  useEffect(() => void refresh(), [refresh]);

  useEffect(() => {
    if (!overview) return;
    if (preserveDraftsOnRefresh.current) {
      preserveDraftsOnRefresh.current = false;
      return;
    }
    const valuesFor = (type: "deposit" | "loss" | "time") =>
      Object.fromEntries(
        periods.map(({ value }) => {
          const policy = getLimits(overview, type).find(
            (scope) => scope.recurringPeriod === value,
          )?.currentPolicy;
          return [
            value,
            type === "time"
              ? secondsToHours(policy?.timeLimitSeconds)
              : formatNumericValue(policy?.amount),
          ];
        }),
      );
    setLimitDrafts({
      deposit: valuesFor("deposit"),
      loss: valuesFor("loss"),
      time: valuesFor("time"),
    });
    setCoolingOffDraft(overview.coolingOff?.currentPolicy?.coolingOffDuration);
    setSelfExclusionDraft(
      overview.selfExclusion?.currentPolicy?.selfExclusionDuration,
    );
  }, [overview]);

  const resetPolicy = useCallback(async () => {
    if (!userId || !resetConfirmationPolicyId) return;
    setResetting(true);
    setResetPolicyId(resetConfirmationPolicyId);
    try {
      await resetResponsibleGamingPolicy(userId, resetConfirmationPolicyId);
      setResetConfirmationPolicyId(undefined);
      await refresh();
    } catch (error) {
      showError(error);
    } finally {
      setResetting(false);
      setResetPolicyId(undefined);
    }
  }, [refresh, resetConfirmationPolicyId, showError, userId]);

  const saveDraft = useCallback(async () => {
    if (!userId || !overview) return true;
    setLimitErrors({});
    let handledLimitError = false;
    const withLimitErrors =
      (
        type: "deposit" | "loss" | "time",
        period: RecurringPeriod,
        action: () => Promise<unknown>,
      ) =>
      async () => {
        try {
          return await action();
        } catch (error) {
          handledLimitError = true;
          handleApiError({
            error,
            t,
            dispatch,
            formErrors: {
              setFieldError: (field, code) => {
                if (
                  field === (type === "time" ? "timeLimitSeconds" : "amount")
                ) {
                  setLimitErrors((current) => ({
                    ...current,
                    [`${type}.${period}`]: code,
                  }));
                } else {
                  showError({
                    code: "ValidationFailed",
                    fieldErrors: [],
                    globalErrors: [{ code }],
                  });
                }
              },
            },
          });
          throw error;
        }
      };
    const actions: (() => Promise<unknown>)[] = [];
    const recurringConfigs = [
      ["deposit", "DEPOSIT_LIMIT"],
      ["loss", "LOSS_LIMIT"],
      ["time", "TIME_LIMIT"],
    ] as const;

    recurringConfigs.forEach(([type, policyType]) => {
      const scopes = getLimits(overview, type);
      periods.forEach(({ value: recurringPeriod }) => {
        const scope = scopes.find(
          (item) => item.recurringPeriod === recurringPeriod,
        );
        const currentPolicy = scope?.currentPolicy;
        const draft = limitDrafts[type][recurringPeriod]?.trim() ?? "";
        const normalizedDraft = formatNumericValue(draft);
        const draftSeconds = hoursToSeconds(normalizedDraft);
        const unchanged =
          type === "time"
            ? draftSeconds === currentPolicy?.timeLimitSeconds
            : normalizedDraft === formatNumericValue(currentPolicy?.amount);
        if (
          !normalizedDraft ||
          unchanged ||
          Number(normalizedDraft) <= 0 ||
          (type === "time" && !Number.isFinite(draftSeconds))
        )
          return;
        if (policyType === "TIME_LIMIT") {
          actions.push(
            withLimitErrors(type, recurringPeriod, () =>
              setResponsibleGamingPolicy(userId, {
                policyType,
                recurringPeriod,
                timeLimitSeconds: draftSeconds,
              }),
            ),
          );
        } else if (currency) {
          actions.push(
            withLimitErrors(type, recurringPeriod, () =>
              setResponsibleGamingPolicy(userId, {
                policyType,
                recurringPeriod,
                amount: normalizedDraft,
                currency,
              }),
            ),
          );
        }
      });
    });

    const currentCoolingOff =
      overview.coolingOff?.currentPolicy?.coolingOffDuration;
    if (coolingOffDraft && coolingOffDraft !== currentCoolingOff) {
      actions.push(() =>
        setResponsibleGamingPolicy(userId, {
          policyType: "COOLING_OFF",
          coolingOffDuration: coolingOffDraft,
        }),
      );
    }
    const currentSelfExclusion =
      overview.selfExclusion?.currentPolicy?.selfExclusionDuration;
    if (selfExclusionDraft && selfExclusionDraft !== currentSelfExclusion) {
      actions.push(() =>
        setResponsibleGamingPolicy(userId, {
          policyType: "SELF_EXCLUSION",
          selfExclusionDuration: selfExclusionDraft,
        }),
      );
    }
    if (!actions.length) return true;

    setMutating(true);
    try {
      for (const action of actions) await action();
      await refresh();
      return true;
    } catch (error) {
      if (!handledLimitError) showError(error);
      preserveDraftsOnRefresh.current = handledLimitError;
      await refresh();
      return false;
    } finally {
      setMutating(false);
    }
  }, [
    coolingOffDraft,
    currency,
    limitDrafts,
    dispatch,
    t,
    overview,
    refresh,
    selfExclusionDraft,
    showError,
    userId,
  ]);

  useEffect(() => {
    onSaveHandlerChange?.(saveDraft);
    return () => onSaveHandlerChange?.(null);
  }, [onSaveHandlerChange, saveDraft]);

  const busy = loading || mutating;
  if (loading && !overview) {
    return (
      <section className={styles.wrapper} aria-busy>
        <ModalTabSkeleton />
      </section>
    );
  }

  return (
    <section className={styles.wrapper} aria-busy={busy}>
      <h3 className={styles.title}>{t("responsibleGaming")}</h3>
      <div className={styles.cardsGrid}>
        <LimitCard
          fieldErrors={limitErrors}
          clearFieldError={clearLimitError}
          id="deposit"
          title={t("depositLimit")}
          tooltip={t("depositLimitTooltip")}
          scopes={getLimits(overview, "deposit")}
          currency={currency}
          busy={busy}
          disabled={disabled}
          values={limitDrafts.deposit}
          onChange={(period, value) =>
            setLimitDrafts((current) => ({
              ...current,
              deposit: { ...current.deposit, [period]: value },
            }))
          }
          onReset={(policyId) => {
            if (!resetting) setResetConfirmationPolicyId(policyId);
          }}
          resetPolicyId={resetPolicyId}
          resetLoading={resetting}
        />
        <LimitCard
          fieldErrors={limitErrors}
          clearFieldError={clearLimitError}
          id="loss"
          title={t("lossLimit")}
          tooltip={t("lossLimitTooltip")}
          scopes={getLimits(overview, "loss")}
          currency={currency}
          busy={busy}
          disabled={disabled}
          values={limitDrafts.loss}
          onChange={(period, value) =>
            setLimitDrafts((current) => ({
              ...current,
              loss: { ...current.loss, [period]: value },
            }))
          }
          onReset={(policyId) => {
            if (!resetting) setResetConfirmationPolicyId(policyId);
          }}
          resetPolicyId={resetPolicyId}
          resetLoading={resetting}
        />
        <LimitCard
          fieldErrors={limitErrors}
          clearFieldError={clearLimitError}
          id="time"
          title={t("timeLimit")}
          tooltip={t("timeLimitTooltip")}
          scopes={getLimits(overview, "time")}
          busy={busy}
          disabled={disabled}
          values={limitDrafts.time}
          onChange={(period, value) =>
            setLimitDrafts((current) => ({
              ...current,
              time: { ...current.time, [period]: value },
            }))
          }
          onReset={(policyId) => {
            if (!resetting) setResetConfirmationPolicyId(policyId);
          }}
          resetPolicyId={resetPolicyId}
          resetLoading={resetting}
        />
        <DurationCard
          id="coolingOff"
          title={t("coolingOff")}
          tooltip={t("coolingOffTooltip")}
          overview={overview?.coolingOff}
          options={coolingOffOptions}
          busy={busy}
          disabled={disabled}
          value={coolingOffDraft}
          onSelect={setCoolingOffDraft}
          onReset={(policyId) => {
            if (!resetting) setResetConfirmationPolicyId(policyId);
          }}
          resetPolicyId={resetPolicyId}
          resetLoading={resetting}
        />
        <DurationCard
          id="selfExclusion"
          title={t("selfExclusion")}
          tooltip={t("selfExclusionTooltip")}
          overview={overview?.selfExclusion}
          options={selfExclusionOptions}
          busy={busy}
          disabled={disabled}
          value={selfExclusionDraft}
          onSelect={setSelfExclusionDraft}
          onReset={(policyId) => {
            if (!resetting) setResetConfirmationPolicyId(policyId);
          }}
          resetPolicyId={resetPolicyId}
          resetLoading={resetting}
        />
      </div>
      <ConfirmTooltip
        id={
          resetConfirmationPolicyId
            ? `responsible-gaming-reset-tooltip-${resetConfirmationPolicyId}`
            : "responsible-gaming-reset-tooltip"
        }
        title={t("resetResponsibleGamingRestriction")}
        confirmText={t("reset")}
        cancelText={t("cancel")}
        isOpen={Boolean(resetConfirmationPolicyId)}
        isLoading={resetting}
        closeOnConfirm={false}
        onToggle={(open) => {
          if (!open && !resetting) setResetConfirmationPolicyId(undefined);
        }}
        onConfirm={() => void resetPolicy()}
        config={{ position: "top", positionStrategy: "fixed" }}
      />
    </section>
  );
};

export default ResponsibleGamingSection;
